import { baseUrl } from './config';
import posthog from './posthog';
import { consumeSignUpSource } from './utils/analytics';

const decode_jwt = (jwt) => {
  const base64Url = jwt.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
  }).join(''));
  return JSON.parse(jsonPayload);
}

class AccessToken {
  sub;
  exp;
  token;
  id;

  constructor(jwt) {
    this.token = jwt;
    const { sub, exp, user_id } = decode_jwt(jwt);
    this.sub = sub;
    this.exp = exp;
    this.id = user_id;
  }

  isValid() {
    return this.exp > Date.now() / 1000;
  }

  getToken() {
    return this.token;
  }
}

class RefreshToken {
  sub;
  exp;
  token;

  constructor(jwt) {
    this.token = jwt;
    const { sub, exp } = decode_jwt(jwt);
    this.sub = sub;
    this.exp = exp;
  }

  isValid() {
    return this.exp > Date.now() / 1000;
  }

  getToken() {
    return this.token;
  }
}

export const signOut = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  window.location.href = '/sign-in';
}

export const getAccessToken = async () => {
  const token = localStorage.getItem('access_token');
  if (!token) return null;

  const accessToken = new AccessToken(token);
  if (accessToken.isValid()) {
    return accessToken;
  }

  // Token is invalid, try to refresh
  const refreshToken = getRefreshToken();
  if (!refreshToken || !refreshToken.isValid()) {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.location.href = '/sign-in';
    return null;
  }

  try {
    const response = await fetch(`${baseUrl}/api/1/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh_token: refreshToken.getToken(),
      }),
    });

    if (!response.ok) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/sign-in';
      return null;
    }

    const { access_token, refresh_token } = await response.json();
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    return new AccessToken(access_token);
  } catch (error) {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.location.href = '/sign-in';
    return null;
  }
}

export const getRefreshToken = () => {
  const token = localStorage.getItem('refresh_token');
  return token ? new RefreshToken(token) : null;
}

export const isSignedIn = () => {
  const accessTokenStr = localStorage.getItem('access_token');
  const refreshTokenStr = localStorage.getItem('refresh_token');

  if (!accessTokenStr || !refreshTokenStr) {
    return false;
  }

  const accessToken = new AccessToken(accessTokenStr);
  const refreshToken = new RefreshToken(refreshTokenStr);

  return accessToken.isValid() || refreshToken.isValid();
}

// --- Added Google Sign-In Handler ---
export const handleGoogleSignIn = async (credentialResponse, navigate) => {
  console.log("Processing Google Sign-In:", credentialResponse);
  try {
    const res = await fetch(`${baseUrl}/api/1/auth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential: credentialResponse.credential }),
    });
    if (!res.ok) {
      const message = await res.text();
      console.error(`Google Sign-In failed: ${message}`);
      alert(`Google Sign-In failed: ${message}`); // Keep alert for now, or replace with better UI feedback
      return; // Indicate failure
    }
    const { access_token, refresh_token, is_new_user } = await res.json();
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);

    // PostHog signup capture if new user
    if (is_new_user) {
      const source = consumeSignUpSource() || 'direct';
      posthog.capture('user_signup', { source, method: 'google' });
    }

    // Check for stored redirect
    const redirect = localStorage.getItem('post_login_redirect');
    if (redirect) {
        localStorage.removeItem('post_login_redirect');
        navigate({ to: redirect });
    } else {
        navigate({ to: '/app/dashboard' });
    }
  } catch (err) {
    // Ensure we catch errors from fetch itself or the throw above
    console.error(`Google Sign-In error: ${err.message}`);
    alert(`Google Sign-In error: ${err.message}`);
    // Rethrow or throw a new standardized error
  }
};
// --- End Google Sign-In Handler ---

export const signup = async (username, password) => {
  try {
    const res = await fetch(`${baseUrl}/api/1/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || data.message || 'Signup failed');
    }

    // PostHog signup capture
    const source = consumeSignUpSource() || 'direct';
    posthog.capture('user_signup', { source, method: 'email' });

    return data;
  } catch (err) {
    console.error(`Signup error: ${err.message}`);
    throw err;
  }
};

// --- Impersonation State Management ---

const IMPERSONATION_KEY = 'impersonation_target_id';

export const startImpersonation = (targetUserId) => {
  if (targetUserId) {
    sessionStorage.setItem(IMPERSONATION_KEY, targetUserId);
    // Optional: Reload or trigger state update if needed immediately
    // window.location.reload(); 
  } else {
    console.error("startImpersonation called with null or undefined targetUserId");
  }
};

export const stopImpersonation = () => {
  sessionStorage.removeItem(IMPERSONATION_KEY);
  // Optional: Reload or trigger state update
  // window.location.reload(); 
};

export const isImpersonating = () => {
  return sessionStorage.getItem(IMPERSONATION_KEY) !== null;
};

export const getImpersonationTargetId = () => {
  return sessionStorage.getItem(IMPERSONATION_KEY);
};

// --- End Impersonation State Management ---

